import { Data, Predicate } from 'effect';

import {
  canonicalSelection,
  emptySelection,
  type ListingSelection,
  type ListingState,
  type TermSelection,
} from '../../capability/productListing/listingSelection';
import type { ListingBase } from '../codec';
import { encodeListingUrl } from '../codec';
import { href } from '../url/listingUrl';
import type { ListingUrlSyntax } from '../url/ListingUrlSyntax';
import { entryForKey, type ListingVocabulary } from '../vocabulary/ListingVocabulary';

export type SeoVerdict = Data.TaggedEnum<{
  Indexable: {};
  NonIndexable: { readonly canonical: ListingSelection };
}>;

export const SeoVerdict = Data.taggedEnum<SeoVerdict>();

/** A term that search engines index: one value of an indexable terms entry. */
const isIndexableTerm = (term: TermSelection, vocabulary: ListingVocabulary): boolean => {
  const entry = entryForKey(vocabulary, term.key);

  return (
    term.values.length === 1 &&
    entry !== undefined &&
    Predicate.isTagged('Terms')(entry) &&
    entry.indexable
  );
};

/** Drops selections that search engines must not index. */
export const nearestIndexable = (
  selection: ListingSelection,
  vocabulary: ListingVocabulary
): ListingSelection =>
  canonicalSelection({
    terms: selection.terms.filter((term) => isIndexableTerm(term, vocabulary)),
    ranges: [],
    sort: emptySelection.sort,
  });

/** Classifies a selection against the listing SEO policy. */
export const classify = (
  selection: ListingSelection,
  vocabulary: ListingVocabulary
): SeoVerdict => {
  const canonical = nearestIndexable(selection, vocabulary);
  if (selection.ranges.length > 0) return SeoVerdict.NonIndexable({ canonical });
  if (!selection.terms.every((term) => isIndexableTerm(term, vocabulary))) {
    return SeoVerdict.NonIndexable({ canonical });
  }
  return SeoVerdict.Indexable();
};

/** Selects the rel value for an HTML filter link. */
export const linkRelFor = (
  next: ListingSelection,
  vocabulary: ListingVocabulary
): 'follow' | 'nofollow' =>
  SeoVerdict.$match(classify(next, vocabulary), {
    Indexable: (): 'follow' => 'follow',
    NonIndexable: (): 'nofollow' => 'nofollow',
  });

export type ListingSeo = {
  readonly robots: { readonly index: boolean; readonly follow: true };
  readonly canonicalHref: string;
};

/** Creates robots and canonical metadata from listing state. */
export const listingSeo = (
  base: ListingBase,
  state: ListingState,
  syntax: ListingUrlSyntax,
  vocabulary: ListingVocabulary
): ListingSeo =>
  SeoVerdict.$match(classify(state.selection, vocabulary), {
    Indexable: (): ListingSeo => {
      const selection = { ...state.selection, sort: emptySelection.sort };
      return {
        robots: { index: true, follow: true },
        canonicalHref: href(encodeListingUrl(base, { ...state, selection }, syntax, vocabulary)),
      };
    },
    NonIndexable: ({ canonical }): ListingSeo => ({
      robots: { index: false, follow: true },
      canonicalHref: href(
        encodeListingUrl(base, { ...state, selection: canonical, page: 1 }, syntax, vocabulary)
      ),
    }),
  });
